"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ARController = void 0;
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const toolbox_1 = require("@streem/toolbox");
const logger_1 = require("../../util/logger");
const utils_1 = require("../wall/utils");
class ARController {
    constructor(config) {
        this.config = config;
        this.localArWallItems = new Set();
        this.haveTrackables = new rxjs_1.BehaviorSubject(false);
        this.source = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('arSource'), (0, utils_1.accumulateAndMapWallEvents)('arSource'), (0, operators_1.shareReplay)(1));
        this.sessionStatus = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('arSessionStatus'), (0, utils_1.accumulateAndMapWallEvents)('arSessionStatus'), (0, operators_1.shareReplay)(1));
        this.scanningRequest = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('arScanningRequest'), (0, utils_1.accumulateAndMapWallEvents)('arScanningRequest'), (0, operators_1.shareReplay)(1));
        this.scanningStatus = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('arScanningStatus'), (0, utils_1.accumulateAndMapWallEvents)('arScanningStatus'), (0, operators_1.shareReplay)(1));
        this.trackables = this.config.mediaController.data.messages.pipe((0, operators_1.filter)(msg => msg.type === 'arTrackables' && 'arTrackables' in msg), (0, operators_1.map)(msg => { var _a; return ((_a = msg.arTrackables) === null || _a === void 0 ? void 0 : _a.trackables) || []; }));
        this.trackableStatus = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('arTrackableStatus'), (0, utils_1.accumulateAndMapWallEvents)('arTrackableStatus'), (0, operators_1.shareReplay)(1));
        this.lightEstimate = this.config.mediaController.data.messages.pipe((0, operators_1.filter)(msg => msg.type === 'arLightEstimate' && 'arLightEstimate' in msg), (0, operators_1.map)(msg => msg.arLightEstimate || undefined));
        this.anchorRequest = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('arAnchorRequest'), (0, utils_1.accumulateAndMapWallEvents)('arAnchorRequest'), (0, operators_1.shareReplay)(1));
        this.anchorStatus = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('arAnchorStatus'), (0, utils_1.accumulateAndMapWallEvents)('arAnchorStatus'), (0, operators_1.shareReplay)(1));
        this.anchors = this.config.mediaController.data.messages.pipe((0, operators_1.filter)(msg => msg.type === 'arAnchors' && 'arAnchors' in msg), (0, operators_1.map)(msg => { var _a; return ((_a = msg.arAnchors) === null || _a === void 0 ? void 0 : _a.anchors) || []; }));
        this.elementGroups = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('elementGroup'), (0, utils_1.accumulateAndMapWallEvents)('elementGroup'), (0, operators_1.shareReplay)(1));
        this.catalogItems = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('catalogItem'), (0, utils_1.accumulateAndMapWallEvents)('catalogItem'), (0, operators_1.shareReplay)(1));
        this.remoteParticipantIsARCapable =
            config.mediaController.remoteParticipantCapabilities.pipe((0, operators_1.map)(capabilities => Boolean((capabilities === null || capabilities === void 0 ? void 0 : capabilities.canSendTrackableTypes) &&
                capabilities.canSendTrackableTypes.length > 0)));
        // Get remote user video track wall items and check if their
        // camera subject has changed. If so, remove any related AR items.
        if (this.config.deleteLocalItemsOnRemoteCameraSubjectChange &&
            this.config.context.remoteUserId) {
            const remoteUserId = this.config.context.remoteUserId.toString();
            this.trackLocalArItems();
            this.config.roomWall.itemsByType
                .pipe((0, utils_1.findInGroup)('videoTrack'), (0, utils_1.accumulateAndMapWallEvents)('videoTrack'), (0, operators_1.map)(videoTrackWallItems => {
                const [latestTrack] = videoTrackWallItems
                    .filter(item => item.ownerUserId === remoteUserId)
                    .reverse();
                return latestTrack;
            }), (0, operators_1.takeWhile)(response => response !== null), (0, operators_1.distinctUntilKeyChanged)('cameraSubject'))
                .subscribe(item => {
                logger_1.logger.info('Video track camera subject changed. Removing related AR wall items', item === null || item === void 0 ? void 0 : item.id);
                this.deleteLocalArItems();
            });
        }
    }
    addScanningRequest(trackableDefinition) {
        (0, toolbox_1.invariant)(this.config.context.remoteUserId && this.config.context.remoteDeviceId, 'Cannot add scanning request without remote user and device ids');
        const request = this.config.roomWall.builder.buildARScanningRequest({
            forUserId: this.config.context.remoteUserId.toString(),
            forDeviceId: this.config.context.remoteDeviceId.toString(),
            trackableDefinition,
        });
        logger_1.logger.info(`Created AR Scanning Request for remoteUserId ${this.config.context.remoteUserId}`, request.id);
        this.config.roomWall.add(request);
        return request.id;
    }
    removeScanningRequest(id) {
        logger_1.logger.info(`Removing AR Scanning Request for remoteUserId ${this.config.context.remoteUserId}`, id);
        this.config.roomWall.delete(id);
    }
    addAnchorRequest(trackableType, trackableId, anchorId, requestedPose) {
        (0, toolbox_1.invariant)(this.config.context.remoteUserId && this.config.context.remoteDeviceId, 'Cannot add anchor request without remote user and device ids');
        const item = this.config.roomWall.builder.buildARAnchorRequest({
            forUserId: this.config.context.remoteUserId.toString(),
            forDeviceId: this.config.context.remoteDeviceId.toString(),
            trackableType,
            trackableId: new Uint8Array(trackableId.split('-').map(s => parseInt(s))),
            anchorId,
            requestedPose,
        });
        logger_1.logger.info(`Adding AR Anchor Request for remoteUserId ${this.config.context.remoteUserId}`, item.id);
        this.config.roomWall.add(item);
    }
    addOrUpdateElementGroup(id, elementGroup) {
        const item = this.config.roomWall.builder.buildElementGroup(id, elementGroup);
        // Disabling this log message since it gets flodded by the new measurement tool. Need to find a balance here.
        // logger.info(
        //     `Adding / Updating Element Group Wall Item for remoteUserId ${this.config.context.remoteUserId}`,
        //     item.id,
        // );
        this.config.roomWall.addOrUpdate(item);
    }
    removeElementGroup(id) {
        this.config.roomWall.delete(id.toString());
    }
    addOrUpdateCatalogItem(id, catalogItem) {
        const item = this.config.roomWall.builder.buildCatalogItem(id, catalogItem);
        logger_1.logger.info(`Adding / Updating Catalog Item WallItem for remoteUserId ${this.config.context.remoteUserId}`, item.id);
        this.config.roomWall.addOrUpdate(item);
    }
    trackLocalArItems() {
        (0, toolbox_1.invariant)(this.config.context.localUserId, 'Expected localUserId to be defined when initializing ARController');
        const localUserId = this.config.context.localUserId.toString();
        const typesToRemove = [
            'arSource',
            'arAnchorRequest',
            'arAnchorStatus',
            'arScanningRequest',
            'arScanningStatus',
            'arTrackableStatus',
            'elementGroup',
            'catalogItem',
            'arSessionStatus',
        ];
        this.config.roomWall.itemsByType
            .pipe((0, operators_1.filter)(group => { var _a; return typesToRemove.includes((_a = group.key) !== null && _a !== void 0 ? _a : ''); }), (0, utils_1.accumulateAndMapWallEvents)(), (0, operators_1.map)((items) => items.filter(item => item.ownerUserId === localUserId)))
            .subscribe(items => {
            items.forEach(item => item.id && this.localArWallItems.add(item.id));
        });
    }
    deleteLocalArItems() {
        return __awaiter(this, void 0, void 0, function* () {
            logger_1.logger.info('Deleting own AR items ', this.localArWallItems);
            this.localArWallItems.forEach(id => this.config.roomWall.delete(id));
            this.localArWallItems.clear();
        });
    }
}
exports.ARController = ARController;
//# sourceMappingURL=ar_controller.js.map